import React, { useEffect, useState } from "react";
import { downloadsService, timeConversion } from "client-app-core";
import { Translate, getTranslation } from "orion-components/i18n";
import AccessTime from "@mui/icons-material/AccessTime";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import CloudDownload from "@mui/icons-material/CloudDownload";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import InsertDriveFileOutlined from "@mui/icons-material/InsertDriveFileOutlined";
import MoreVert from "@mui/icons-material/MoreVert";
import WarningAmber from "@mui/icons-material/WarningAmber";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import { useSelector } from "react-redux";
import { useDownloads } from "./DownloadsContext";

export { reconcileDownloadChange, sortDownloads } from "./DownloadsContext";

const styles = {
	wrapper: {
		boxSizing: "border-box",
		color: "#fff",
		display: "flex",
		flexDirection: "column",
		fontFamily: "Roboto, sans-serif",
		height: "calc(100% - 48px)",
		minHeight: 0,
		overflow: "hidden",
		padding: "10px 0 0"
	},
	panelHeader: {
		alignItems: "flex-start",
		borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
		display: "flex",
		flexShrink: 0,
		gap: 8,
		justifyContent: "space-between",
		margin: "0 24px",
		padding: "2px 0 12px"
	},
	notice: {
		color: "#C7CDD3",
		fontSize: 12,
		lineHeight: "18px",
		minWidth: 0
	},
	centerState: {
		alignItems: "center",
		color: "#B5B9BE",
		display: "flex",
		flexDirection: "column",
		flex: 1,
		gap: 12,
		justifyContent: "center",
		minHeight: 220,
		padding: "0 24px",
		textAlign: "center"
	},
	list: {
		display: "flex",
		flex: 1,
		flexDirection: "column",
		minHeight: 0,
		overflowY: "auto",
		padding: "0 24px"
	},
	item: {
		alignItems: "center",
		borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
		display: "grid",
		gap: 8,
		gridTemplateColumns: "32px minmax(0, 1fr) auto",
		minHeight: 76,
		padding: "10px 0"
	},
	statusIcon: {
		alignItems: "center",
		display: "flex",
		height: 32,
		justifyContent: "center",
		width: 32
	},
	main: {
		minWidth: 0
	},
	fileName: {
		color: "#FFFFFF",
		fontSize: 15,
		fontWeight: 600,
		lineHeight: "20px",
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap"
	},
	details: {
		alignItems: "center",
		display: "flex",
		gap: 7,
		marginTop: 3,
		minWidth: 0
	},
	source: {
		color: "#C7CDD3",
		fontSize: 12,
		lineHeight: "17px",
		minWidth: 0,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap"
	},
	status: {
		borderRadius: 4,
		flexShrink: 0,
		fontSize: 11,
		fontWeight: 600,
		lineHeight: "16px",
		padding: "2px 7px",
		whiteSpace: "nowrap"
	},
	meta: {
		color: "#C7CDD3",
		fontSize: 12,
		whiteSpace: "nowrap"
	},
	actions: {
		alignItems: "center",
		display: "grid",
		gridTemplateColumns: "max-content min-content"
	},
	errorText: {
		color: "#E57373",
		fontSize: 12,
		lineHeight: "16px",
		marginTop: 4,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap"
	}
};

const statusStyles = {
	Pending: {
		color: "#E1A82D",
		background: "rgba(225, 168, 45, 0.16)",
		icon: AccessTime
	},
	"In Progress": {
		color: "#64B5F6",
		background: "rgba(100, 181, 246, 0.16)",
		icon: CircularProgress
	},
	Completed: {
		color: "#81C784",
		background: "rgba(129, 199, 132, 0.16)",
		icon: CheckCircleOutline
	},
	Downloaded: {
		color: "#64B5F6",
		background: "rgba(100, 181, 246, 0.16)",
		icon: CloudDownload
	},
	Failed: {
		color: "#E57373",
		background: "rgba(229, 115, 115, 0.16)",
		icon: WarningAmber
	},
	Deleted: {
		color: "#B5B9BE",
		background: "rgba(181, 185, 190, 0.16)",
		icon: InsertDriveFileOutlined
	}
};

const statusTranslationKeys = {
	Pending: "pending",
	"In Progress": "inProgress",
	Completed: "completed",
	Downloaded: "downloaded",
	Failed: "failed",
	Deleted: "deleted"
};

export const getStatusTranslationKey = status => {
	const translationKey = statusTranslationKeys[status] || statusTranslationKeys.Pending;
	return `global.dock.downloads.status.${translationKey}`;
};

export const formatDate = (value, timeFormatPreference = "12-hour", locale = "en") => {
	if (!value) return "";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "";

	const today = new Date();
	const yesterday = new Date();
	yesterday.setDate(today.getDate() - 1);

	if (date.toDateString() === today.toDateString()) {
		const useTwentyFourHourTime = timeFormatPreference === "24-hour";
		return date.toLocaleTimeString(locale, {
			hour: useTwentyFourHourTime ? "2-digit" : "numeric",
			hour12: !useTwentyFourHourTime,
			minute: "2-digit"
		});
	}

	if (date.toDateString() === yesterday.toDateString()) {
		return getTranslation("global.dock.downloads.yesterday");
	}

	return date.toLocaleDateString([], { month: "short", day: "numeric" });
};

export const getLifecycleMetadata = download => {
	if (download.completedDate) return { status: "Completed", date: download.completedDate };
	if (download.failedDate) return { status: "Failed", date: download.failedDate };
	return { status: download.status || "Pending", date: download.createdDate };
};

export const getDisplayStatus = download => {
	if (download.status === "Completed" && download.downloadCount > 0) {
		return "Downloaded";
	}
	return download.status || "Pending";
};

export const formatFullTimestamp = (value, timeFormatPreference = "12-hour", locale = "en") => {
	if (!value) return "";
	return timeConversion.convertToUserTime(value, `full_${timeFormatPreference}`, locale) || "";
};

export const getLifecycleTooltip = (download, fullTimestamp) => {
	const { status, date } = getLifecycleMetadata(download);
	if (!date) return "";
	return `${getTranslation(getStatusTranslationKey(status))} ${fullTimestamp(date)}`;
};

export const getStatusTooltip = (download, fullTimestamp) => {
	if (download.status === "Failed") {
		return download.errorMessage || "";
	}

	if (download.status === "Completed" && download.downloadCount > 0 && download.lastDownloadedDate) {
		return `${getTranslation("global.dock.downloads.downloadedAt")} ${fullTimestamp(download.lastDownloadedDate)}`;
	}

	if (download.status === "Completed" && download.completedDate) {
		return `${getTranslation("global.dock.downloads.completedAt")} ${fullTimestamp(download.completedDate)}`;
	}

	return "";
};

export const getApplicationDisplayName = (applications, appId) => {
	const application = applications?.find(app => app.appId === appId);
	return (application?.overrideName && application?.orgAppName) || application?.name || appId;
};

const DownloadsPanel = () => {
	const applications = useSelector(state => state.session?.user?.profile?.applications || []);
	const timeFormatPreference = useSelector(state => state.appState?.global?.timeFormat || "12-hour");
	const locale = useSelector(state => state.i18n?.locale || "en");
	const [retentionDays, setRetentionDays] = useState(7);
	const { downloads, loading, error: downloadsError, refreshDownloads } = useDownloads();
	const [actionError, setActionError] = useState(null);
	const [panelMenuAnchor, setPanelMenuAnchor] = useState(null);
	const [actionAnchor, setActionAnchor] = useState(null);
	const [selectedDownload, setSelectedDownload] = useState(null);
	const error = actionError || downloadsError;

	const handleDownload = download => {
		if (!download.downloadUrl) return;

		const link = document.createElement("a");
		link.href = download.downloadUrl;
		link.download = download.fileName || "download";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	const openActions = (event, download) => {
		setActionAnchor(event.currentTarget);
		setSelectedDownload(download);
	};

	const closeActions = () => {
		setActionAnchor(null);
		setSelectedDownload(null);
	};

	const deleteDownload = download => {
		if (!download) return;
		closeActions();
		setActionError(null);
		downloadsService.deleteDownload(download.id, err => {
			if (err) {
				setActionError(err);
				return;
			}
			refreshDownloads();
		});
	};

	const clearCompleted = () => {
		setPanelMenuAnchor(null);
		setActionError(null);
		downloadsService.clearCompletedDownloads(err => {
			if (err) {
				setActionError(err);
				return;
			}
			refreshDownloads();
		});
	};

	useEffect(() => {
		const mounted = { current: true };
		downloadsService.getConfig((err, response) => {
			if (!mounted.current || err) return;
			const configuredRetentionDays = Number(response?.retentionDays);
			if (Number.isFinite(configuredRetentionDays) && configuredRetentionDays > 0) {
				setRetentionDays(configuredRetentionDays);
			}
		});
		return () => {
			mounted.current = false;
		};
	}, []);

	const hasCompletedDownloads = downloads.some(download => download.status === "Completed");

	let panelContent;
	if (loading) {
		panelContent = (
			<div style={styles.centerState}>
				<CircularProgress size={28} data-testid="orion-components-dock-downloads-downloads-panel-circular-progress" />
				<Translate value="global.dock.downloads.loading" />
			</div>
		);
	} else if (error) {
		panelContent = (
			<div style={styles.centerState}>
				<ErrorOutline />
				<Translate value="global.dock.downloads.error" />
			</div>
		);
	} else if (!downloads.length) {
		panelContent = (
			<div style={styles.centerState}>
				<CloudDownload />
				<Translate value="global.dock.downloads.empty" />
			</div>
		);
	} else {
		panelContent = (
			<div style={styles.list}>
				{downloads.map(download => {
					const displayStatus = getDisplayStatus(download);
					const statusStyle = statusStyles[displayStatus] || statusStyles.Pending;
					const StatusIcon = statusStyle.icon;
					const canShowDownloadAction = download.status === "Completed" && download.downloadUrl;
					const sourceName = getApplicationDisplayName(applications, download.sourceApp);
					const lifecycleDate = getLifecycleMetadata(download).date;
					const fullTimestamp = value => formatFullTimestamp(value, timeFormatPreference, locale);
					const statusTooltip = getStatusTooltip(download, fullTimestamp);
					const lifecycleTooltip = getLifecycleTooltip(download, fullTimestamp);

					return (
						<div style={styles.item} key={download.id}>
							<div style={{ ...styles.statusIcon, color: statusStyle.color }}>
								{displayStatus === "In Progress" ? (
									<CircularProgress size={24} thickness={3} style={{ color: statusStyle.color }} data-testid="orion-components-dock-downloads-downloads-panel-circular-progress-2" />
								) : (
									<StatusIcon style={{ fontSize: 26 }} />
								)}
							</div>
							<div style={styles.main}>
								<div
									title={download.fileName || getTranslation("global.dock.downloads.untitled")}
									style={styles.fileName}
								>
									{download.fileName || getTranslation("global.dock.downloads.untitled")}
								</div>
								<div style={styles.details}>
									<div
										title={sourceName || getTranslation("global.dock.downloads.unknownSource")}
										style={styles.source}
									>
										{sourceName || getTranslation("global.dock.downloads.unknownSource")}
									</div>
									{statusTooltip ? (
										<Tooltip title={statusTooltip} data-testid="orion-components-dock-downloads-downloads-panel-tooltip">
											<div
												style={{
													...styles.status,
													background: statusStyle.background,
													color: statusStyle.color
												}}
											>
												<Translate value={getStatusTranslationKey(displayStatus)} />
											</div>
										</Tooltip>
									) : (
										<div
											style={{
												...styles.status,
												background: statusStyle.background,
												color: statusStyle.color
											}}
										>
											<Translate value={getStatusTranslationKey(displayStatus)} />
										</div>
									)}
								</div>
								{download.status === "Failed" && download.errorMessage && (
									<div title={download.errorMessage} style={styles.errorText}>
										{download.errorMessage}
									</div>
								)}
							</div>
							<Box sx={{ ...styles.actions, columnGap: 1 }}>
								<Tooltip title={lifecycleTooltip} data-testid="orion-components-dock-downloads-downloads-panel-tooltip-2">
									<div style={{ ...styles.meta, justifySelf: "end", textAlign: "right" }}>
										{formatDate(lifecycleDate, timeFormatPreference, locale)}
									</div>
								</Tooltip>
								<Box sx={{ alignItems: "center", display: "flex" }}>
									<Tooltip title={getTranslation("global.dock.downloads.download")} data-testid="orion-components-dock-downloads-downloads-panel-tooltip-3">
										<span style={{ display: "inline-flex" }}>
											<IconButton
												disabled={!canShowDownloadAction}
												onClick={() => handleDownload(download)}
												size="small"
												sx={{
													color: "#C7CDD3",
													"&.Mui-disabled": { color: "#6F7479" }
												}} data-testid="orion-components-dock-downloads-downloads-panel-icon-button"
											>
												<FileDownloadOutlined fontSize="small" />
											</IconButton>
										</span>
									</Tooltip>
									<Tooltip title={getTranslation("global.dock.downloads.actions")} data-testid="orion-components-dock-downloads-downloads-panel-tooltip-4">
										<IconButton
											onClick={event => openActions(event, download)}
											size="small"
											sx={{ color: "#C7CDD3" }} data-testid="orion-components-dock-downloads-downloads-panel-icon-button-2"
										>
											<MoreVert fontSize="small" />
										</IconButton>
									</Tooltip>
								</Box>
							</Box>
						</div>
					);
				})}
			</div>
		);
	}

	return (
		<div style={styles.wrapper}>
			<div style={styles.panelHeader}>
				<div style={styles.notice}>
					<div>
						<Translate value="global.dock.downloads.retentionNotice" count={retentionDays} />
					</div>
					<div>
						<Translate value="global.dock.downloads.notificationNotice" />
					</div>
				</div>
				<Tooltip title={getTranslation("global.dock.downloads.actions")} data-testid="orion-components-dock-downloads-downloads-panel-tooltip-5">
					<IconButton
						onClick={event => setPanelMenuAnchor(event.currentTarget)}
						size="small"
						sx={{ color: "#C7CDD3", flexShrink: 0, height: 30, padding: "4px", width: 30 }} data-testid="orion-components-dock-downloads-downloads-panel-icon-button-3"
					>
						<MoreVert fontSize="small" />
					</IconButton>
				</Tooltip>
			</div>
			{panelContent}
			<Menu
				anchorEl={panelMenuAnchor}
				onClose={() => setPanelMenuAnchor(null)}
				open={Boolean(panelMenuAnchor)}
				sx={{ zIndex: 2600 }}
				PaperProps={{
					sx: {
						backgroundColor: "#38373A",
						color: "#FFF"
					}
				}} data-testid="orion-components-dock-downloads-downloads-panel-menu"
			>
				<MenuItem disabled={!hasCompletedDownloads} onClick={clearCompleted} data-testid="orion-components-dock-downloads-downloads-panel-menu-item">
					<DeleteOutline sx={{ fontSize: 19, marginRight: 1 }} />
					<Translate value="global.dock.downloads.clearCompleted" />
				</MenuItem>
			</Menu>
			<Menu
				anchorEl={actionAnchor}
				onClose={closeActions}
				open={Boolean(actionAnchor)}
				sx={{ zIndex: 2600 }}
				PaperProps={{
					sx: {
						backgroundColor: "#38373A",
						color: "#FFF"
					}
				}} data-testid="orion-components-dock-downloads-downloads-panel-menu-2"
			>
				<MenuItem onClick={() => deleteDownload(selectedDownload)} data-testid="orion-components-dock-downloads-downloads-panel-menu-item-2">
					<Translate value="global.dock.downloads.delete" />
				</MenuItem>
			</Menu>
		</div>
	);
};

export default DownloadsPanel;
